import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useContent } from "../lib/content";
import {
  CalendarClock, MessageSquare, Send, MessagesSquare, TrendingUp,
  CheckCircle2, ArrowRight, DoorOpen,
} from "lucide-react";

/**
 * The one screen that answers "what needs me today?".
 *
 * Everything here is already somewhere else in the portal — the point is that
 * an agent shouldn't have to visit six screens to find out nothing is on fire.
 * Sections disappear when empty, so a quiet day looks quiet.
 */

const GOAL_LABELS = {
  launch: "Just listed",
  open_house: "Open house",
  feature: "Feature spotlight",
  price: "Price improvement",
};

const PLATFORM_LABELS = { instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok" };

function Section({ icon, title, count, children, tone }) {
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "16px 20px", borderBottom: "1px solid var(--border)",
          background: tone === "urgent" ? "rgba(184,92,71,0.06)" : "var(--surface-alt)",
        }}
      >
        <span style={{ color: tone === "urgent" ? "var(--secondary)" : "var(--primary)", display: "flex" }}>
          {icon}
        </span>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0, flex: 1 }}>{title}</h2>
        {count > 0 && (
          <span
            style={{
              fontSize: 12, fontWeight: 600, minWidth: 22, textAlign: "center",
              padding: "2px 8px", borderRadius: 999, color: "#fff",
              background: tone === "urgent" ? "var(--secondary)" : "var(--primary)",
            }}
          >
            {count}
          </span>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({ to, primary, secondary, trailing, testid }) {
  const body = (
    <>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)" }}>{primary}</div>
        {secondary && (
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2, lineHeight: 1.5 }}>
            {secondary}
          </div>
        )}
      </div>
      {trailing && (
        <div style={{ fontSize: 13, color: "var(--text-muted)", whiteSpace: "nowrap", flexShrink: 0 }}>
          {trailing}
        </div>
      )}
      {to && <ArrowRight size={15} style={{ color: "var(--text-muted)", flexShrink: 0 }} />}
    </>
  );
  const style = {
    display: "flex", alignItems: "center", gap: 14,
    padding: "13px 20px", borderTop: "1px solid var(--border)",
    textDecoration: "none", color: "inherit",
  };
  return to
    ? <Link to={to} style={style} data-testid={testid}>{body}</Link>
    : <div style={style} data-testid={testid}>{body}</div>;
}

export default function AdminToday() {
  const { user } = useAuth();
  const { content } = useContent();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/admin/today");
        if (alive) setData(res.data);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const firstName = (user?.name || "").trim().split(/\s+/)[0]
    || content?.branding?.agent_first_name || "there";
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  if (loading) {
    return (
      <div className="container-x" style={{ padding: "40px 0" }}>
        <p style={{ color: "var(--text-muted)" }}>Loading your day…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container-x" style={{ padding: "40px 0" }}>
        <div className="card" style={{ padding: 28 }}>
          <p style={{ margin: 0, color: "var(--text-muted)" }}>
            Couldn't load today's summary. Your <Link to="/admin/clients" style={{ color: "var(--primary)" }}>client list</Link> is still there.
          </p>
        </div>
      </div>
    );
  }

  const { counts, upcoming, unanswered, unshared_feedback: unshared, due_posts: duePosts, stale } = data;
  const allClear = !counts.upcoming && !counts.unanswered && !counts.unshared_feedback
    && !counts.due_posts && !counts.stale;

  return (
    <div className="container-x" style={{ padding: "36px 0 64px" }} data-testid="admin-today">
      <div style={{ marginBottom: 26 }}>
        <div className="overline">Today</div>
        <h1 className="font-serif" style={{ fontSize: 32, fontWeight: 500, margin: "6px 0 6px" }}>
          {greeting}, {firstName}.
        </h1>
        <p style={{ color: "var(--text-muted)", fontSize: 15, margin: 0 }}>
          {data.needs_attention > 0
            ? `${data.needs_attention} thing${data.needs_attention === 1 ? "" : "s"} could use you today.`
            : "Nothing urgent on your plate."}
        </p>
      </div>

      {allClear && (
        <div className="card" style={{ padding: 40, textAlign: "center" }} data-testid="today-all-clear">
          <CheckCircle2 size={26} style={{ color: "var(--primary)", marginBottom: 12 }} />
          <h2 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "0 0 8px" }}>
            You're all caught up
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14.5, margin: 0, lineHeight: 1.6 }}>
            No showings coming up, no one waiting on a reply, nothing scheduled to post.
          </p>
            {(data.active_listings || []).length > 0 && (
              <div
                data-testid="today-active-listings"
                style={{ marginTop: 22, textAlign: "left" }}
              >
                <div className="overline" style={{ marginBottom: 6 }}>
                  On the market
                </div>
                {data.active_listings.map((l) => (
                  <Link
                    key={l.id}
                    to={`/admin/seller/${l.id}`}
                    style={{
                      display: "block", padding: "9px 0",
                      borderTop: "1px solid var(--border)",
                      color: "var(--text)", textDecoration: "none",
                    }}
                  >
                    <div style={{ fontSize: 14.5 }}>{l.address || l.name}</div>
                    {l.address ? (
                      <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                        {l.name}
                      </div>
                    ) : null}
                  </Link>
                ))}
              </div>
            )}
            <div
              style={{
                marginTop: 20, display: "flex", gap: 10,
                flexWrap: "wrap", justifyContent: "center",
              }}
            >
              <Link to="/admin/clients" className="btn-secondary" style={{ fontSize: 13.5 }}>
                All clients
              </Link>
              <Link to="/admin/submissions" className="btn-ghost" style={{ fontSize: 13.5 }}>
                Submissions
              </Link>
            </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {counts.unanswered > 0 && (
          <Section icon={<MessageSquare size={17} />} title="Waiting on your reply"
                   count={counts.unanswered} tone="urgent">
            {unanswered.map((m) => (
              <Row
                key={m.message_id}
                to="/admin/messages"
                testid={`today-message-${m.message_id}`}
                primary={m.subject || "Question from your seller"}
                secondary={`${m.seller} — "${m.preview}"`}
                trailing={
                  m.days_waiting === 0 ? "Today"
                    : m.days_waiting === 1 ? "1 day"
                    : `${m.days_waiting} days`
                }
              />
            ))}
          </Section>
        )}

        {counts.upcoming > 0 && (
          <Section icon={<CalendarClock size={17} />} title="Coming up"
                   count={counts.upcoming} tone={counts.today > 0 ? "urgent" : undefined}>
            {upcoming.map((s, i) => (
              <Row
                key={`${s.seller_id}-${s.date}-${s.start_time}-${i}`}
                to={s.seller_id ? `/admin/seller/${s.seller_id}` : undefined}
                testid={`today-showing-${i}`}
                primary={
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                    {s.kind === "open_house" && <DoorOpen size={14} style={{ color: "var(--text-muted)" }} />}
                    {s.seller}
                  </span>
                }
                secondary={[
                  s.kind === "open_house" ? "Open house" : "Showing",
                  s.buyer_brokerage,
                ].filter(Boolean).join(" · ")}
                trailing={`${s.day}${s.start_time ? `, ${s.start_time}` : ""}`}
              />
            ))}
          </Section>
        )}

        {counts.due_posts > 0 && (
          <Section icon={<Send size={17} />} title="Ready to post" count={counts.due_posts}>
            {duePosts.map((c) => (
              <Row
                key={c.campaign_id}
                to={c.seller_id ? `/admin/seller/${c.seller_id}` : undefined}
                testid={`today-post-${c.campaign_id}`}
                primary={`${GOAL_LABELS[c.goal] || "Post"} — ${c.seller}`}
                secondary={PLATFORM_LABELS[c.platform] || c.platform}
                trailing={c.is_today ? "Today" : (c.scheduled_at || "").slice(5, 10)}
              />
            ))}
          </Section>
        )}

        {counts.unshared_feedback > 0 && (
          <Section icon={<MessagesSquare size={17} />} title="Feedback you haven't shared"
                   count={counts.unshared_feedback}>
            {unshared.map((f) => (
              <Row
                key={f.feedback_id}
                to={f.seller_id ? `/admin/seller/${f.seller_id}` : undefined}
                testid={`today-feedback-${f.feedback_id}`}
                primary={f.seller}
                secondary={`"${f.preview}"`}
                trailing={f.date}
              />
            ))}
          </Section>
        )}

        {counts.stale > 0 && (
          <Section icon={<TrendingUp size={17} />} title="Listings going quiet" count={counts.stale}>
            {stale.map((s, i) => (
              <Row
                key={`${s.seller_id}-${i}`}
                to={s.seller_id ? `/admin/seller/${s.seller_id}` : undefined}
                testid={`today-stale-${i}`}
                primary={s.seller}
                secondary={s.never
                  ? "No activity numbers entered yet — their Home Activity page is empty"
                  : `Activity numbers last updated ${s.days} days ago`}
              />
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}
